import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Check, TrendingUp, TrendingDown, Calculator } from "lucide-react";

const categoriasReceita = [
  "Venda de Grãos", "Venda de Gado", "Venda de Leite", "Arrendamento",
  "Prestação de Serviços", "Outras Receitas"
];

const categoriasDespesa = [
  "Insumos", "Sementes", "Fertilizantes", "Defensivos", "Combustível",
  "Mão de Obra", "Manutenção", "Energia", "Aluguel", "Financiamento",
  "Impostos", "Seguros", "Frete", "Armazenagem", "Consultoria", "Outras Despesas"
];

const meses = [
  { key: "valor_jan", label: "Jan" },
  { key: "valor_fev", label: "Fev" },
  { key: "valor_mar", label: "Mar" },
  { key: "valor_abr", label: "Abr" },
  { key: "valor_mai", label: "Mai" },
  { key: "valor_jun", label: "Jun" },
  { key: "valor_jul", label: "Jul" },
  { key: "valor_ago", label: "Ago" },
  { key: "valor_set", label: "Set" },
  { key: "valor_out", label: "Out" },
  { key: "valor_nov", label: "Nov" },
  { key: "valor_dez", label: "Dez" }
];

export default function OrcamentoForm({ orcamento, centrosCusto, safras, onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const anoAtual = new Date().getFullYear();

  const [formData, setFormData] = useState({
    nome: "",
    ano: anoAtual,
    tipo: "despesa",
    categoria: "",
    centro_custo_id: "",
    safra_id: "",
    valor_jan: 0, valor_fev: 0, valor_mar: 0, valor_abr: 0,
    valor_mai: 0, valor_jun: 0, valor_jul: 0, valor_ago: 0,
    valor_set: 0, valor_out: 0, valor_nov: 0, valor_dez: 0,
    limite_alerta: 10,
    observacoes: ""
  });

  const [distribuicaoTipo, setDistribuicaoTipo] = useState("manual");
  const [valorAnual, setValorAnual] = useState(0);

  useEffect(() => {
    if (orcamento) {
      setFormData({
        nome: orcamento.nome || "",
        ano: orcamento.ano || anoAtual,
        tipo: orcamento.tipo || "despesa",
        categoria: orcamento.categoria || "",
        centro_custo_id: orcamento.centro_custo_id || "",
        safra_id: orcamento.safra_id || "",
        valor_jan: orcamento.valor_jan || 0,
        valor_fev: orcamento.valor_fev || 0,
        valor_mar: orcamento.valor_mar || 0,
        valor_abr: orcamento.valor_abr || 0,
        valor_mai: orcamento.valor_mai || 0,
        valor_jun: orcamento.valor_jun || 0,
        valor_jul: orcamento.valor_jul || 0,
        valor_ago: orcamento.valor_ago || 0,
        valor_set: orcamento.valor_set || 0,
        valor_out: orcamento.valor_out || 0,
        valor_nov: orcamento.valor_nov || 0,
        valor_dez: orcamento.valor_dez || 0,
        limite_alerta: orcamento.limite_alerta || 10,
        observacoes: orcamento.observacoes || ""
      });
      setValorAnual(orcamento.valor_total || 0);
    }
  }, [orcamento]);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data) => {
      if (orcamento) {
        return agrogestao.entities.Orcamento.update(orcamento.id, data);
      }
      return agrogestao.entities.Orcamento.create({ ...data, propriedade_id: propriedadeId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      onSuccess();
    }
  });

  const calcularTotal = () => {
    return meses.reduce((acc, m) => acc + (parseFloat(formData[m.key]) || 0), 0);
  };

  const distribuirValor = (tipo) => {
    if (!valorAnual || valorAnual <= 0) return;
    
    const novoFormData = { ...formData };
    
    if (tipo === "uniforme") {
      const valorMensal = valorAnual / 12;
      meses.forEach(m => {
        novoFormData[m.key] = Math.round(valorMensal * 100) / 100;
      });
    } else if (tipo === "safra") {
      // Concentra nos meses de safra (set-mar para plantio/colheita)
      const mesesSafra = ["valor_set", "valor_out", "valor_nov", "valor_dez", "valor_jan", "valor_fev", "valor_mar"];
      const valorPorMes = valorAnual / mesesSafra.length;
      meses.forEach(m => {
        novoFormData[m.key] = mesesSafra.includes(m.key) ? Math.round(valorPorMes * 100) / 100 : 0;
      });
    } else if (tipo === "trimestral") {
      const valorTrimestre = valorAnual / 4;
      const mesesTrimestre = ["valor_mar", "valor_jun", "valor_set", "valor_dez"];
      meses.forEach(m => {
        novoFormData[m.key] = mesesTrimestre.includes(m.key) ? Math.round(valorTrimestre * 100) / 100 : 0;
      });
    }
    
    setFormData(novoFormData);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const total = calcularTotal();
    mutation.mutate({
      ...formData,
      centro_custo_id: formData.centro_custo_id ? formData.centro_custo_id : null,
      safra_id: formData.safra_id ? formData.safra_id : null,
      valor_jan: parseFloat(formData.valor_jan) || 0,
      valor_fev: parseFloat(formData.valor_fev) || 0,
      valor_mar: parseFloat(formData.valor_mar) || 0,
      valor_abr: parseFloat(formData.valor_abr) || 0,
      valor_mai: parseFloat(formData.valor_mai) || 0,
      valor_jun: parseFloat(formData.valor_jun) || 0,
      valor_jul: parseFloat(formData.valor_jul) || 0,
      valor_ago: parseFloat(formData.valor_ago) || 0,
      valor_set: parseFloat(formData.valor_set) || 0,
      valor_out: parseFloat(formData.valor_out) || 0,
      valor_nov: parseFloat(formData.valor_nov) || 0,
      valor_dez: parseFloat(formData.valor_dez) || 0,
      valor_total: total,
      limite_alerta: parseFloat(formData.limite_alerta) || 10
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const categorias = formData.tipo === "receita" ? categoriasReceita : categoriasDespesa;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tipo */}
      <div className="flex gap-2 p-1 bg-stone-100 rounded-xl">
        <button
          type="button"
          onClick={() => setFormData(p => ({...p, tipo: 'receita', categoria: ''}))}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            formData.tipo === 'receita' ? 'bg-green-500 text-white shadow' : 'text-stone-600'
          }`}
        >
          <TrendingUp className="w-4 h-4" /> Receita
        </button>
        <button
          type="button"
          onClick={() => setFormData(p => ({...p, tipo: 'despesa', categoria: ''}))}
          className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
            formData.tipo === 'despesa' ? 'bg-red-500 text-white shadow' : 'text-stone-600'
          }`}
        >
          <TrendingDown className="w-4 h-4" /> Despesa
        </button>
      </div>

      {/* Dados básicos */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nome do Orçamento *</Label>
          <Input
            placeholder="Ex: Orçamento Insumos 2025"
            value={formData.nome}
            onChange={(e) => setFormData({...formData, nome: e.target.value})}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Ano *</Label>
          <Select value={formData.ano.toString()} onValueChange={(v) => setFormData({...formData, ano: parseInt(v)})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[anoAtual - 1, anoAtual, anoAtual + 1, anoAtual + 2].map(a => (
                <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Categoria *</Label>
          <Select value={formData.categoria} onValueChange={(v) => setFormData({...formData, categoria: v})}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione..." />
            </SelectTrigger>
            <SelectContent>
              {categorias.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Limite de Alerta (%)</Label>
          <Input
            type="number"
            placeholder="10"
            value={formData.limite_alerta}
            onChange={(e) => setFormData({...formData, limite_alerta: e.target.value})}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Centro de Custo</Label>
          <Select value={formData.centro_custo_id} onValueChange={(v) => setFormData({...formData, centro_custo_id: v === 'none' ? '' : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Opcional" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhum</SelectItem>
              {centrosCusto.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Safra</Label>
          <Select value={formData.safra_id} onValueChange={(v) => setFormData({...formData, safra_id: v === 'none' ? '' : v})}>
            <SelectTrigger>
              <SelectValue placeholder="Opcional" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Nenhuma</SelectItem>
              {safras.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Distribuição automática */}
      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-blue-600" />
          <Label className="text-blue-700 font-medium">Distribuição Automática</Label>
        </div>
        <div className="flex gap-3">
          <Input
            type="number"
            step="0.01"
            placeholder="Valor anual total"
            value={valorAnual}
            onChange={(e) => setValorAnual(parseFloat(e.target.value) || 0)}
            className="flex-1"
          />
          <Button type="button" variant="outline" size="sm" onClick={() => distribuirValor("uniforme")}>
            Uniforme
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => distribuirValor("safra")}>
            Safra
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => distribuirValor("trimestral")}>
            Trimestral
          </Button>
        </div>
      </div>

      {/* Valores mensais */}
      <div className="space-y-3">
        <Label>Valores Mensais</Label>
        <div className="grid grid-cols-4 gap-3">
          {meses.map(m => (
            <div key={m.key} className="space-y-1">
              <Label className="text-xs text-stone-500">{m.label}</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0,00"
                value={formData[m.key]}
                onChange={(e) => setFormData({...formData, [m.key]: e.target.value})}
              />
            </div>
          ))}
        </div>
        <div className={`p-3 rounded-lg text-right ${formData.tipo === 'receita' ? 'bg-green-100' : 'bg-red-100'}`}>
          <span className="text-sm text-stone-600">Total Anual: </span>
          <span className={`text-lg font-bold ${formData.tipo === 'receita' ? 'text-green-700' : 'text-red-700'}`}>
            {formatCurrency(calcularTotal())}
          </span>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea
          placeholder="Notas sobre este orçamento..."
          value={formData.observacoes}
          onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
        />
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={mutation.isPending}
          className={`flex-1 ${formData.tipo === 'receita' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'}`}
        >
          {mutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <><Check className="w-4 h-4 mr-2" /> {orcamento ? "Atualizar" : "Criar Orçamento"}</>
          )}
        </Button>
      </div>
    </form>
  );
}
